// ─────────────────────────────────────────────────────────
// Einstellungen → Layout & Textvorlagen → Anfangs- & Schlusstexte
// ─────────────────────────────────────────────────────────
// plancraft-Modell: zwei Sektionen (Anfangstexte/Schlusstexte), je ein
// Akkordeon über 8 Dokumenttypen, pro Typ eine LISTE von Textblöcken
// (hinzufügen/löschen). Prefix dt-. Rechte: layout:read / layout:write.
// Sprache: alle sichtbaren Texte über t() — Keys in i18n/de.json + en.json.
import { icon, escapeHtml } from '../../utils/dom.js';
import { saveDocumentTexts } from '../../core/org.js';
import { t } from '../../i18n/index.js';

const DOC_TYPES = [
  'angebot',
  'kostenvoranschlag',
  'auftragsbestaetigung',
  'lieferschein',
  'rechnung',
  'abschlagsrechnung',
  'schlussrechnung',
  'gutschrift',
];
const SECTIONS = ['opening', 'closing'];

const keyOf = (section, type) => section + ':' + type;

// leere Struktur, falls noch nichts geladen
// (Variable heisst bewusst NICHT `t` — das ist die i18n-Funktion aus dem Import oben.)
function texts(s) {
  const dt = s.documentTexts || {};
  return { opening: dt.opening || {}, closing: dt.closing || {} };
}
function blocks(s, section, type) {
  const arr = texts(s)[section][type];
  return Array.isArray(arr) ? arr : [];
}

// ── Kopf-Button „Speichern" ───────────────────────────────
export function documentTextsHeadButton(ctx) {
  if (!ctx.can('layout:write')) return '';
  const saving = ctx.s.dtSaving;
  return `<button class="cd-btn-primary" data-action="dt-save" ${saving ? 'disabled' : ''}>${escapeHtml(saving ? t('common.saving') : t('common.save'))}</button>`;
}

// ── DOM → State (vor jedem Redraw, damit Eingaben erhalten bleiben) ──
// Liest nur die aktuell aufgeklappten Typen (nur die haben Inputs im DOM).
export function syncFromDom(s) {
  if (!(s.dtOpen instanceof Set)) return;
  const cur = texts(s);
  s.documentTexts = { opening: { ...cur.opening }, closing: { ...cur.closing } };
  for (const openKey of s.dtOpen) {
    const [section, type] = openKey.split(':');
    const len = blocks(s, section, type).length;
    const vals = [];
    for (let i = 0; i < len; i++) {
      const el = document.getElementById(`dt-${section}-${type}-${i}`);
      vals.push(el ? el.value : '');
    }
    s.documentTexts[section][type] = vals;
  }
}

// ── Rendering ─────────────────────────────────────────────
function renderType(s, section, type, dis) {
  const open = s.dtOpen instanceof Set && s.dtOpen.has(keyOf(section, type));
  const list = blocks(s, section, type);
  const chev = open ? 'expand_more' : 'chevron_right';
  let bodyHtml = '';
  if (open) {
    const rows = list.map((val, i) => `
      <div class="cd-dt-block">
        <input class="cd-input cd-dt-block-in" id="dt-${section}-${type}-${i}" type="text" value="${escapeHtml(val)}" placeholder="${escapeHtml(t('settings.documentTexts.placeholder'))}" ${dis}>
        <button class="cd-dt-del" data-action="dt-del" data-section="${section}" data-type="${type}" data-index="${i}" title="${escapeHtml(t('settings.documentTexts.removeBlock'))}" ${dis}>${icon('close', 16)}</button>
      </div>`).join('');
    const addBtn = dis ? '' : `<button class="cd-dt-add" data-action="dt-add" data-section="${section}" data-type="${type}">${escapeHtml(t('settings.documentTexts.addBlock'))}</button>`;
    bodyHtml = `<div class="cd-dt-acc-body">${rows}${addBtn}</div>`;
  }
  const count = list.filter((x) => x && x.trim()).length;
  return `
    <div class="cd-dt-acc-row ${open ? 'open' : ''}">
      <button class="cd-dt-acc-head" data-action="dt-toggle" data-section="${section}" data-type="${type}">
        <span class="cd-dt-chev">${icon(chev, 18)}</span>
        <span class="cd-dt-acc-label">${escapeHtml(t('docTypes.' + type))}</span>
        ${count ? `<span class="cd-dt-badge">${count}</span>` : ''}
      </button>
      ${bodyHtml}
    </div>`;
}

export function renderDocumentTextsSection(ctx) {
  const { s, can } = ctx;
  if (!can('layout:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  if (!(s.dtOpen instanceof Set)) s.dtOpen = new Set();

  const dis = s.dtSaving || !can('layout:write') ? 'disabled' : '';
  const cards = SECTIONS.map((section) => {
    const rows = DOC_TYPES.map((type) => renderType(s, section, type, dis)).join('');
    return `
      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('notes', 18)}<h3>${escapeHtml(t('settings.documentTexts.' + section))}</h3></div>
        <div class="cd-dt-acc">${rows}</div>
      </div>`;
  }).join('');

  return `
    <div class="cd-emp">
      <p class="cd-sc-lead">${escapeHtml(t('settings.documentTexts.lead'))}</p>
      ${cards}
      <div class="cd-emp-info cd-sc-note">${icon('info', 18)}<span>${escapeHtml(t('settings.documentTexts.note'))}</span></div>
    </div>`;
}

// ── Aktions-Handler ───────────────────────────────────────
export function handleDocumentTextsAction(a, el, ctx) {
  const { s, draw, reload } = ctx;
  if (!a || a.indexOf('dt-') !== 0) return false;
  if (!(s.dtOpen instanceof Set)) s.dtOpen = new Set();
  const d = el.dataset;

  if (a === 'dt-toggle') {
    syncFromDom(s);
    const k = keyOf(d.section, d.type);
    if (s.dtOpen.has(k)) s.dtOpen.delete(k); else s.dtOpen.add(k);
    draw();
    return true;
  }
  if (a === 'dt-add') {
    if (!ctx.can('layout:write')) return true;
    syncFromDom(s);
    s.dtOpen.add(keyOf(d.section, d.type));
    s.documentTexts[d.section][d.type] = [...blocks(s, d.section, d.type), ''];
    draw();
    return true;
  }
  if (a === 'dt-del') {
    if (!ctx.can('layout:write')) return true;
    syncFromDom(s);
    const arr = [...blocks(s, d.section, d.type)];
    arr.splice(Number(d.index), 1);
    s.documentTexts[d.section][d.type] = arr;
    draw();
    return true;
  }
  if (a === 'dt-save') {
    if (!ctx.can('layout:write')) return true;
    syncFromDom(s);
    s.dtSaving = true; draw();
    saveDocumentTexts(s.documentTexts)
      .then(() => { s.dtSaving = false; if (reload) reload(); else draw(); })
      .catch((err) => { s.dtSaving = false; s.error = err.message || t('common.saveFailed'); draw(); });
    return true;
  }
  return false;
}
